// ─── Layout Debug Overlay ─────────────────────────────────────
// Activated by appending `?debug=layout` to any public invitation
// URL. Dumps the exact data a designer needs to compare desktop
// vs. phone rendering:
//   • frontend bundle hash (proves whether two devices run the same build)
//   • viewport width
//   • isMobile (<768)
//   • for every canvas element: type, id, layout.mobile, layout.desktop,
//     and the bounding rect the browser computed
//
// Designed for QA only — read-only, no network, no side effects.
// Stays hidden unless explicitly enabled.

import React, { useEffect, useState } from 'react';
import { getCurrentBundleHash } from '../../lib/utils/buildVersionCheck';

const BUILD_HASH = getCurrentBundleHash() || 'dev';

// FNV-1a 32-bit. Tiny, stable, no deps. Gives an 8-char hex digest
// that's good enough to eyeball-diff two pasted reports.
const fnv1a = (str) => {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
};

// Build the canonical payload that feeds the diagnostic hash. We
// intentionally include ONLY fields that describe the rendered context
// (what was loaded + how it laid out) and exclude per-request volatile
// values like server_now and _ts so the same render produces the same
// hash on every poll.
const computeDiagHash = (snap) => {
  if (!snap) return '00000000';
  const id = snap.identity || {};
  const lang = (typeof document !== 'undefined' && document.documentElement.lang) || '';
  const elements = (snap.elements || []).map((e) => [
    e.id, e.type, e.left, e.top, e.w, e.h, e.absW, e.absH,
  ]);
  const sectionInfo = (snap.sectionInfo || []).map((s) => [s.v, s.w, s.h]);
  const payload = JSON.stringify({
    build: BUILD_HASH,
    event_id: id.event_id || '',
    slug: id.slug || '',
    updated_at: id.updated_at || '',
    is_published: id.is_published === true,
    language: lang,
    viewport: snap.viewport,
    isMobile: snap.isMobile,
    sectionCount: snap.sectionCount,
    sectionInfo,
    elements,
  });
  return fnv1a(payload);
};

export default function LayoutDebugOverlay() {
  const [snap, setSnap] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [copyState, setCopyState] = useState('idle'); // 'idle' | 'copied' | 'failed'
  const [fallbackText, setFallbackText] = useState(null);

  useEffect(() => {
    const sample = () => {
      try {
        // Phase 1+2: only count true section *elements* (not the wrapper div
        // that TemplateRenderer adds with data-section-type="collage"). The
        // wrapper would otherwise double-count every section and every
        // element inside it.
        const candidates = document.querySelectorAll('[data-section-type="collage"], [data-section-version="section-v1"]');
        const sections = Array.from(candidates).filter(
          (el) => el.tagName === 'SECTION' || el.hasAttribute('data-section-version')
        );
        const out = [];
        const seenElementIds = new Set();
        const sectionInfo = [];
        const conflicts = [];
        sections.forEach((sec) => {
          const secRect = sec.getBoundingClientRect();
          sectionInfo.push({
            v: sec.getAttribute('data-section-version') || 'legacy',
            w: Math.round(secRect.width),
            h: Math.round(secRect.height),
            heightPx: sec.getAttribute('data-section-height-px') || undefined,
            heightSource: sec.getAttribute('data-section-height-source') || undefined,
          });
          const els = sec.querySelectorAll('[data-testid^="v1-el-"], [data-testid^="collage-el-"]');
          els.forEach((el) => {
            const id = el.getAttribute('data-element-id') || (el.getAttribute('data-testid') || '').replace(/^v1-el-|^collage-el-/, '');
            if (seenElementIds.has(id)) return; // dedupe across nested wrappers
            seenElementIds.add(id);
            const r = el.getBoundingClientRect();
            const hasConflict = el.getAttribute('data-bounds-conflict') === 'true';
            const conflictAxes = el.getAttribute('data-bounds-conflict-axes') || '';
            if (hasConflict) {
              conflicts.push({ id, axes: conflictAxes });
            }
            out.push({
              id,
              type: el.getAttribute('data-element-type') || 'el',
              // Rendered rect normalized to the section box.
              left: secRect.width ? +((r.left - secRect.left) / secRect.width * 100).toFixed(1) : 0,
              top: secRect.height ? +((r.top - secRect.top) / secRect.height * 100).toFixed(1) : 0,
              w: secRect.width ? +(r.width / secRect.width * 100).toFixed(1) : 0,
              h: secRect.height ? +(r.height / secRect.height * 100).toFixed(1) : 0,
              absW: Math.round(r.width),
              absH: Math.round(r.height),
              boundsSourceY: el.getAttribute('data-bounds-source-y') || undefined,
              conflict: hasConflict ? conflictAxes : undefined,
            });
          });
        });
        setSnap({
          viewport: window.innerWidth,
          isMobile: window.innerWidth < 768,
          sectionCount: sections.length,
          sectionInfo,
          conflicts,
          elements: out,
          when: new Date().toLocaleTimeString(),
          // Pulled from the global stash set by PublicInvite /
          // PublicInviteRsvp when the API responds. Lets QA verify
          // that two devices loaded the SAME server payload by
          // comparing event_id, updated_at, and server_now.
          identity: (typeof window !== 'undefined' && window.__sitoaResponseIdentity) || null,
          fetchUrl: (typeof window !== 'undefined' && window.__sitoaLastFetchUrl) || null,
        });
      } catch { /* never throw */ }
    };

    sample();
    const interval = setInterval(sample, 1500);
    return () => clearInterval(interval);
  }, []);

  if (hidden || !snap) return null;

  // Format the current snapshot as a plain-text bug-report blob —
  // designers paste this into chat instead of describing what they
  // saw. Schema kept stable so it diffs cleanly between devices.
  const diagHash = computeDiagHash(snap);

  const buildReport = () => {
    const id = snap.identity || {};
    const lines = [
      'Sitoa Debug Report',
      `Diag: ${diagHash}`,
      '',
      `Build: ${BUILD_HASH}`,
      `API: ${snap.fetchUrl || '(not captured)'}`,
      `Event: ${id.event_id || '?'}`,
      `Slug: ${id.slug || '?'}`,
      `Published: ${id.is_published === true ? 'yes' : id.is_published === false ? 'no' : '?'}`,
      `Updated: ${id.updated_at || '?'}`,
      `Server: ${id.server_now || '?'}`,
      `Preview token: …${id.preview_token_suffix || '-'}`,
      `Language: ${(typeof document !== 'undefined' && document.documentElement.lang) || '?'}`,
      '',
      `Viewport: ${snap.viewport}px`,
      `isMobile: ${snap.isMobile}`,
      `Sections: ${snap.sectionCount}`,
      ...(Array.isArray(snap.sectionInfo)
        ? snap.sectionInfo.map((s, i) => {
            const src = s.heightSource ? ` · src=${s.heightSource}` : '';
            const px = s.heightPx ? ` · saved=${s.heightPx}px` : '';
            return `Section ${i + 1}: ${s.v} · ${s.w}×${s.h}px${px}${src}`;
          })
        : []),
      ...(Array.isArray(snap.conflicts) && snap.conflicts.length > 0
        ? ['', `⚠ Layout conflicts: ${snap.conflicts.length}`,
           ...snap.conflicts.map((c) => `  ${c.id} · axes=${c.axes}`)]
        : []),
      `Sampled at: ${snap.when}`,
      '',
      'Elements:',
    ];
    if (snap.elements.length === 0) {
      lines.push('  (none detected)');
    } else {
      for (const e of snap.elements) {
        lines.push(`${e.type} ${e.id}`);
        lines.push(`  left: ${e.left}%`);
        lines.push(`  top: ${e.top}%`);
        lines.push(`  w: ${e.w}%`);
        lines.push(`  h: ${e.h}%`);
        lines.push(`  abs: width ${e.absW}px, height ${e.absH}px`);
      }
    }
    return lines.join('\n');
  };

  const handleCopy = async () => {
    const report = buildReport();
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(report);
        setCopyState('copied');
        setTimeout(() => setCopyState('idle'), 2200);
        return;
      }
      throw new Error('no clipboard API');
    } catch {
      // Fallback: surface the report inline so the designer can
      // long-press / select-all on phones where Clipboard API is
      // blocked (iOS Safari requires a user gesture chain that the
      // overlay's polling can occasionally invalidate).
      setFallbackText(report);
      setCopyState('failed');
    }
  };

  return (
    <div
      className="fixed bottom-2 left-2 right-2 sm:right-auto sm:max-w-[420px] z-[400] rounded-lg bg-black/90 text-white text-[10.5px] leading-tight font-mono p-2.5 shadow-2xl overflow-auto"
      style={{ maxHeight: '40vh' }}
      data-testid="layout-debug-overlay"
    >
      <div className="flex items-center justify-between mb-1.5 sticky top-0 bg-black/95 -mt-2.5 -mx-2.5 px-2.5 pt-2 pb-1 border-b border-white/15">
        <span className="font-bold text-violet-300">DEBUG · layout</span>
        <div className="flex items-center gap-1.5">
          <button
            onClick={handleCopy}
            className={`text-[10px] font-medium px-2 py-0.5 rounded transition-colors ${
              copyState === 'copied'
                ? 'bg-emerald-600 text-white'
                : copyState === 'failed'
                ? 'bg-amber-600 text-white'
                : 'bg-violet-600 text-white hover:bg-violet-700'
            }`}
            data-testid="layout-debug-copy"
            title="Copy debug report to clipboard"
          >
            {copyState === 'copied' ? 'Copied ✓' : copyState === 'failed' ? 'Select below' : 'Copy report'}
          </button>
          <button
            onClick={() => setHidden(true)}
            className="text-white/60 hover:text-white text-[14px] leading-none px-1"
            aria-label="Hide debug overlay"
          >×</button>
        </div>
      </div>
      <div className="space-y-1">
        <div>build: <b className="text-amber-300">{BUILD_HASH}</b> · diag: <b className="text-fuchsia-300" data-testid="layout-debug-diag-hash">{diagHash}</b></div>
        <div>viewport: <b>{snap.viewport}px</b> · isMobile: <b>{String(snap.isMobile)}</b> · sections: {snap.sectionCount} · {snap.when}</div>
        {Array.isArray(snap.sectionInfo) && snap.sectionInfo.length > 0 && (
          <div className="text-fuchsia-200/90" data-testid="layout-debug-section-info">
            {snap.sectionInfo.map((s, i) => (
              <div key={i}>
                section {i + 1}: <b>{s.v}</b> · {s.w}×{s.h}px
                {s.heightSource && <span className="text-fuchsia-300/70"> · src={s.heightSource}</span>}
              </div>
            ))}
          </div>
        )}
        {Array.isArray(snap.conflicts) && snap.conflicts.length > 0 && (
          <div className="text-red-300 font-bold" data-testid="layout-debug-conflicts">
            ⚠ {snap.conflicts.length} layout conflict{snap.conflicts.length === 1 ? '' : 's'}:
            {snap.conflicts.map((c) => (
              <div key={c.id} className="font-normal text-red-200">  {c.id} ({c.axes})</div>
            ))}
          </div>
        )}
        {snap.identity && (
          <div className="text-emerald-300/90 break-all">
            <div>event: <b>{(snap.identity.event_id || '').slice(0, 8)}</b>… · updated: <b>{snap.identity.updated_at || '?'}</b></div>
            <div>token: <b>…{snap.identity.preview_token_suffix || '-'}</b> · server: {snap.identity.server_now}</div>
          </div>
        )}
        {snap.fetchUrl && (
          <div className="text-sky-300/90 break-all">
            <div>api: {snap.fetchUrl.replace(/^https?:\/\/[^/]+/, '')}</div>
          </div>
        )}
        {snap.elements.length === 0 && <div className="opacity-60 italic">no canvas elements detected</div>}
        {snap.elements.map((e, i) => (
          <div key={`${e.id}-${i}`} className="pl-1 border-l border-white/15 py-0.5">
            <span className="text-violet-300">{e.type}</span> <span className="text-white/60">{e.id}</span>
            <div className="pl-2 text-white/80">
              left {e.left}% · top {e.top}% · w {e.w}% ({e.absW}px) · h {e.h}% ({e.absH}px)
            </div>
          </div>
        ))}
        {/* Clipboard fallback — phones occasionally reject the
            programmatic write because the user gesture chain breaks
            during polling. Show the report inline so they can
            long-press/select-all manually. */}
        {fallbackText && (
          <div className="mt-2 pt-2 border-t border-white/15">
            <div className="text-amber-300 mb-1">
              Could not copy automatically. Select and copy the text below:
            </div>
            <textarea
              readOnly
              value={fallbackText}
              onFocus={(e) => e.target.select()}
              className="w-full h-32 bg-black/60 text-white text-[10px] font-mono p-1.5 rounded border border-white/20 resize-y"
              data-testid="layout-debug-fallback"
            />
          </div>
        )}
      </div>
    </div>
  );
}
